<div data-testid="stock-delivery-order-page" class="p-6 flex-table-page routed-flex-table-page">
  <!-- Search Form -->
  <app-search-form
    data-testid="stock-delivery-order-search-form"
    [searchFormat]="searchFormat()"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'stock-delivery-order-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      @if (canCreate()) {
        <app-button
          [testId]="'stock-delivery-order-add-btn'"
          [label]="
            'outbound_management.actions.create_outbound_order' | translate
          "
          icon="add"
          type="info"
          (emitOnClick)="onAddNew()"
        ></app-button>
      }
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="table-section flex-table-section">
    <advanced-data-table
      data-testid="stock-delivery-order-table"
      #advancedTable
      [dataSource]="dataSource()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [totalRecords]="totalRecords()"
      [sortField]="sortField()"
      [sortOrder]="sortOrder()"
      scrollHeight="flex"
      [showInternalColumnConfigButton]="false"
      class="w-full"
      (pageChange)="onPageChange($event)"
      (sortChange)="onSortChange($event)"
    >
      <!-- Custom Template for Outbound Type, Status, Reference Code, and Creation Time -->
      <ng-template
        #customTemplate
        let-value
        let-rowData="rowData"
        let-column="column"
      >
        @if (column.prop === "outType") {
          <!-- Outbound Type -->
          <span class="text-xs text-gray-700">
            {{ getOutboundTypeLabel(rowData.outType) }}
          </span>
        } @else if (column.prop === "status") {
          <!-- Status -->
          <span class="status-badge" [class]="'status-' + rowData.status">
            {{ getStatusLabel(rowData.status, rowData.outType) }}
          </span>
        } @else if (column.prop === "referenceCode") {
          <!-- Reference Code: orderCode for sales order, frontRecordCode for transfer order -->
          <span class="text-xs text-gray-700">
            {{ getReferenceCode(rowData) }}
          </span>
        } @else if (column.prop === "receiver") {
          <!-- Receiver -->
          <span class="text-xs text-gray-700">
            {{ getReceiverDisplay(rowData.receiver) }}
          </span>
        } @else if (column.prop === "creationDate") {
          <!-- Creation Time -->
          <span class="text-xs text-gray-700">
            {{ formatCreationTime(value) }}
          </span>
        } @else if (column.prop === "outLogicWarehouseName") {
          <!-- Warehouse/Store Name: outLogicWarehouseName for transfer order, shopName for sales order -->
          <span class="text-xs text-gray-700">
            {{ getWarehouseOrStoreName(rowData) }}
          </span>
        }
      </ng-template>

      <!-- Actions Template -->
      <ng-template #actionsTemplate let-record>
        <div class="inline-flex items-center gap-2 whitespace-nowrap">
          <!-- View button -->
          @if (canRead()) {
            <span
              [attr.data-testid]="'stock-delivery-order-action-view-' + record.id"
              (click)="onViewDetails(record); $event.stopPropagation()"
              class="action-link action-view cursor-pointer"
            >
              {{ "common.actions.view" | translate }}
            </span>
          }

          <!-- Others button - visible if there are enabled actions -->
          <!-- For APPROVED, PICKED and PARTIALLY_SHIPPED, always show button (API will be called on click) -->
          <!-- For other statuses, show if there are more than 1 action -->
          @if (
            hasEnabledOutboundActions(record) &&
            (record.status === "APPROVED" ||
              record.status === "PICKED" ||
              record.status === "PARTIALLY_SHIPPED" ||
              getOutboundDeliveryActions(record).length > 1)
          ) {
            <button
              [attr.data-testid]="'stock-delivery-order-action-others-' + record.id"
              type="button"
              class="action-link action-others cursor-pointer"
              aria-haspopup="menu"
              (click)="toggleMenu($event, record)"
            >
              {{ "common.actions.others" | translate }}
            </button>
          }
        </div>
      </ng-template>
    </advanced-data-table>
  </div>
</div>

<!-- Single Menu for All Actions -->
<p-menu
  data-testid="stock-delivery-order-actions-menu"
  #actionMenu
  [popup]="true"
  [model]="currentMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
  (onHide)="onMenuHide()"
></p-menu>

<!-- Confirmation Dialog for Approve -->
<app-confirmation-dialog
  [testId]="'stock-delivery-order-approve-dialog'"
  [visible]="showApproveDialog()"
  [title]="'outbound_management.dialog.confirm_approve.title' | translate"
  [message]="'outbound_management.dialog.confirm_approve.message' | translate"
  [buttons]="approveDialogButtons"
  (emitOnClick)="onApproveConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Reject -->
<app-confirmation-dialog
  [testId]="'stock-delivery-order-reject-dialog'"
  [visible]="showRejectDialog()"
  [title]="'outbound_management.dialog.confirm_reject.title' | translate"
  [message]="'outbound_management.dialog.confirm_reject.message' | translate"
  [buttons]="rejectDialogButtons"
  (emitOnClick)="onRejectConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Start Picking -->
<app-confirmation-dialog
  [testId]="'stock-delivery-order-start-picking-dialog'"
  [visible]="showStartPickingDialog()"
  [title]="'outbound_management.dialog.confirm_start_picking.title' | translate"
  [message]="
    'outbound_management.dialog.confirm_start_picking.message' | translate
  "
  [buttons]="startPickingDialogButtons"
  (emitOnClick)="onStartPickingConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Confirm Shipping -->
<app-confirmation-dialog
  [testId]="'stock-delivery-order-confirm-shipping-dialog'"
  [visible]="showConfirmShippingDialog()"
  [title]="'outbound_management.dialog.confirm_shipping.title' | translate"
  [message]="'outbound_management.dialog.confirm_shipping.message' | translate"
  [buttons]="confirmShippingDialogButtons"
  (emitOnClick)="onConfirmShippingConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Cancel -->
<app-confirmation-dialog
  [testId]="'stock-delivery-order-cancel-dialog'"
  [visible]="showCancelDialog()"
  [title]="'outbound_management.dialog.confirm_cancel.title' | translate"
  [message]="'outbound_management.dialog.confirm_cancel.message' | translate"
  [buttons]="cancelDialogButtons"
  (emitOnClick)="onCancelConfirm($event)"
></app-confirmation-dialog>

<p-toast data-testid="stock-delivery-order-toast"></p-toast>
